import React, { useState, useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Button, Modal } from '../../../../Components/uielements';
import {
  ModalButtonWrapper,
  ModalGap,
  OutlineButton,
} from '../../../../services/settings/appStyle/global.style';
import { IntlMessages } from '../../../../helper/langHelper';
import {
  getAssignableUsers,
  assignCheckListToUser,
} from '../../../../services/bioplus-services/checkList';
import { FormSection, InputGroup } from '../checkList.style';
import { ModalWrapper } from '../../EmailTemplate/emailTemplate.style';
import { ModalLabel } from '../../UserV2/user.style';

const AssignCheckListToUser = ({ onClose, checkListId, checkListTitle, companyId }) => {
  const queryClient = useQueryClient();
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [showResultModal, setShowResultModal] = useState(false);
  const [resultMessage, setResultMessage] = useState({ type: '', message: '' });

  // Fetch assignable users
  const { data: users, isLoading: loadingUsers } = useQuery(
    ['assignableUsers', checkListId],
    () => getAssignableUsers({ questionnaireId: checkListId, companyId }),
    {
      enabled: !!checkListId,
      onError: (error) => {
        setResultMessage({
          type: 'error',
          message: error?.response?.data?.message || 'Failed to load users',
        });
        setShowResultModal(true);
      },
    }
  );

  const mutation = useMutation((payload) => assignCheckListToUser(payload), {
    onSuccess: () => {
      queryClient.invalidateQueries('checkLists');
      setResultMessage({
        type: 'success',
        message: 'Checklist successfully assigned to selected users',
      });
      setShowResultModal(true);
    },
    onError: (error) => {
      setResultMessage({
        type: 'error',
        message: error?.response?.data?.message || 'Failed to assign checklist',
      });
      setShowResultModal(true);
    },
  });

  const handleSubmit = () => {
    if (selectedUsers.length === 0) {
      setResultMessage({
        type: 'error',
        message: 'Please select at least one member',
      });
      setShowResultModal(true);
      return;
    }

    const payload = {
      questionnaireId: checkListId,
      users: selectedUsers,
      companyId,
    };

    mutation.mutate(payload);
  };

  const handleUserClick = (userId) => {
    setSelectedUsers((prev) => {
      if (prev.includes(userId)) {
        return prev.filter((id) => id !== userId);
      }
      return [...prev, userId];
    });
  };

  const handleResultModalClose = () => {
    setShowResultModal(false);
    if (resultMessage.type === 'success') {
      onClose();
    }
  };

  if (showResultModal) {
    return (
      <Modal
        isOpen={true}
        onClose={handleResultModalClose}
        isUppercase={true}
        title={resultMessage.type === 'success' ? 'Success' : 'Error'}
      >
        <ModalWrapper>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <p
              style={{
                fontSize: '16px',
                marginBottom: '20px',
                color: resultMessage.type === 'success' ? '#7AEAA7' : '#ff4d4f',
              }}
            >
              {resultMessage.message}
            </p>
          </div>
          <ModalButtonWrapper style={{ marginTop: '0px' }}>
            <Button varient="normal" onClick={handleResultModalClose}>
              OK
            </Button>
          </ModalButtonWrapper>
        </ModalWrapper>
      </Modal>
    );
  }

  return (
    <Modal isOpen={true} onClose={onClose} isUppercase={true} title="Assign CheckList to Member">
      <ModalWrapper>
        <FormSection>
          <InputGroup>
            <ModalLabel>CheckList Title</ModalLabel>
            <div
              style={{
                padding: '8px 12px',
                borderRadius: '4px',
                border: '1px solid #494c6a',
                marginBottom: '16px',
                color: 'white',
              }}
            >
              {checkListTitle}
            </div>
          </InputGroup>

          <InputGroup>
            <ModalLabel>Select Members *</ModalLabel>
            {loadingUsers ? (
              <div
                style={{
                  padding: '40px 12px',
                  color: '#999',
                  textAlign: 'center',
                  border: '1px solid #494c6a',
                  borderRadius: '4px',
                  minHeight: '200px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                Loading users...
              </div>
            ) : (
              <div
                style={{
                  width: '100%',
                  maxHeight: '300px',
                  overflowY: 'auto',
                  border: '1px solid #494c6a',
                  borderRadius: '4px',
                }}
              >
                {users?.map((user) => (
                  <div
                    key={user.id}
                    onClick={() => handleUserClick(user.id)}
                    style={{
                      padding: '10px 12px',
                      cursor: 'pointer',
                      borderBottom: '1px solid #333',
                      backgroundColor: selectedUsers.includes(user.id)
                        ? 'rgba(82, 196, 26, 0.2)'
                        : 'transparent',
                      color: selectedUsers.includes(user.id) ? '#7AEAA7' : 'white',
                      transition: 'all 0.2s ease',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                    }}
                    onMouseEnter={(e) => {
                      if (!selectedUsers.includes(user.id)) {
                        e.currentTarget.style.backgroundColor = 'rgba(255, 255, 255, 0.05)';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!selectedUsers.includes(user.id)) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <div
                      style={{
                        width: '18px',
                        height: '18px',
                        border: `2px solid ${
                          selectedUsers.includes(user.id) ? '#52c41a' : '#494c6a'
                        }`,
                        borderRadius: '3px',
                        backgroundColor: selectedUsers.includes(user.id)
                          ? '#52c41a'
                          : 'transparent',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      {selectedUsers.includes(user.id) && (
                        <svg
                          width="12"
                          height="12"
                          viewBox="0 0 12 12"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M10 3L4.5 8.5L2 6"
                            stroke="white"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      )}
                    </div>
                    <span>
                      {user.firstName} {user.lastName} ({user.email})
                    </span>
                  </div>
                ))}
              </div>
            )}
            {!loadingUsers && users && users.length === 0 && (
              <div
                style={{
                  padding: '20px',
                  textAlign: 'center',
                  color: '#999',
                  border: '1px solid #494c6a',
                  borderRadius: '4px',
                }}
              >
                No users available to assign
              </div>
            )}
          </InputGroup>
        </FormSection>

        <ModalGap />

        <ModalButtonWrapper style={{ marginTop: '0px' }}>
          <OutlineButton type="button" onClick={onClose}>
            <IntlMessages id="modal.button.cancel" />
          </OutlineButton>
          <Button
            varient="normal"
            onClick={handleSubmit}
            disabled={mutation?.isLoading || loadingUsers}
          >
            Assign
            {mutation?.isLoading && '...'}
          </Button>
        </ModalButtonWrapper>
      </ModalWrapper>
    </Modal>
  );
};

export default AssignCheckListToUser;
